// src/app/modules/social/services/player-visibility.service.ts
import { Injectable } from '@angular/core';
import { Viewport } from 'pixi-viewport';
import { Player } from '../social-pixi-canvas/player';
import { throttle } from 'lodash';
import { Loggable } from 'app/utils/logging/loggable';
import { Container } from 'pixi.js';

@Injectable({
  providedIn: 'root',
})
export class PlayerVisibilityService extends Loggable {
  private checkInterval = 100; // Default check interval in ms
  private padding = 200; // Padding to consider player visibility
  private hiddenPlayers = new Set<string>(); // Track players that were hidden due to culling

  public constructor() {
    super();
    this.setDisplayName('PlayerVisibilityService');
  }

  // Debounced function that will be configured when initialized
  public processInvisiblePlayers = throttle(
    (params: { participants: Map<string, Player>; viewport: Viewport; healthCheckFn: (player: Player) => void }) => {
      this.removeInvisiblePlayers(params);
    },
    this.checkInterval,
  );

  // Method to set the check interval - currently unused
  public setCheckInterval(): void {
    this.processInvisiblePlayers = throttle(
      (params: { participants: Map<string, Player>; viewport: Viewport; healthCheckFn: (player: Player) => void }) => {
        this.removeInvisiblePlayers(params);
      },
      this.checkInterval,
    );
  }

  private removeInvisiblePlayers({
    healthCheckFn,
    participants,
    viewport,
  }: {
    participants: Map<string, Player>;
    viewport: Viewport;
    healthCheckFn: (player: Player) => void;
  }): void {
    Array.from(participants.values()).forEach((player) => {
      const isVisible = this.isPlayerVisible(player, viewport);
      const userId = player.userId;
      const parent = player.parent;

      // cull players that are not visible
      if (player.wrapperContainer && !isVisible) {
        player.visible = false;
        player.destroyUser();

        if (parent) {
          this.hiddenPlayers.add(userId);
          this.info(`Culling player ${userId} - out of view`);

          parent.removeChild(player);
        }
      } else if (this.hiddenPlayers.has(userId) && isVisible) {
        // Player was previously hidden but now in view - restore them fully
        player.visible = true;

        // Re-add to the parent container if needed
        if (parent === null && viewport.parent) {
          // Find the roomContainer (typically roomContainer is a child of viewport)
          const roomContainer = viewport.parent.getChildByName('roomContainer');
          if (roomContainer) {
            (roomContainer as Container).addChild(player);
            this.info(`Re-added player ${userId} to room container`);
          }
        }

        // Rebuild the player through health check
        healthCheckFn(player);
        this.hiddenPlayers.delete(userId);
        this.info(`Restoring player ${userId} - back in view`);
      } else if (!player.wrapperContainer && isVisible) {
        // Player doesn't have a container but is visible - recreate them
        player.visible = true;

        // Re-add to the parent container if needed
        if (parent === null && viewport.parent) {
          const roomContainer = viewport.parent.getChildByName('roomContainer');
          if (roomContainer) {
            (roomContainer as Container).addChild(player);
            this.info(`Re-added player ${userId} to room container`);
          }
        }

        healthCheckFn(player);
        this.info(`Rebuilding player ${userId} - visible but missing container`);
      }
    });
  }

  /**
   * Determine whether a player should be visible based on viewport bounds.
   * Public method to allow other components to check visibility.
   * @param player Player to check visibility for
   * @param viewport Current viewport
   * @returns boolean indicating if player should be visible
   */
  public isPlayerVisible(player: Player, viewport: Viewport): boolean {
    // Skip invalid players or viewport
    if (!player || !viewport) {
      return false;
    }

    // Use cached bounds if available
    const bounds = viewport.getVisibleBounds();
    if (!bounds) {
      this.warn('No bounds available from viewport');
      return true; // Default to visible if we can't determine bounds
    }

    // Default padding if scale is missing or invalid
    let padding = this.padding;
    if (viewport.scale && viewport.scale.x) {
      // Adjust padding based on zoom level
      padding = this.padding * (1 / viewport.scale.x);
    }

    // Ensure all required player properties exist
    const playerX = player.x ?? 0;
    const playerY = player.y ?? 0;
    const playerWidth = player.width ?? 0;
    const playerHeight = player.height ?? 0;

    // Fast rectangle intersection test with extra safety
    return (
      playerX + playerWidth > bounds.left - padding &&
      playerX - padding < bounds.right + padding &&
      playerY + playerHeight > bounds.top - padding &&
      playerY - padding < bounds.bottom + padding
    );
  }

  public healthCheckAll({
    healthCheckFn,
    participants,
    player,
  }: {
    player?: Player;
    participants: Map<string, Player>;
    healthCheckFn: (player: Player) => void;
  }): void {
    if (player) {
      healthCheckFn(player);
    }

    Array.from(participants.values())
      .filter((p) => p.visible || this.hiddenPlayers.has(p.userId))
      .forEach((p) => {
        healthCheckFn(p);
        // Also ensure previously hidden players are properly restored if they should be visible now
        if (this.hiddenPlayers.has(p.userId)) {
          this.hiddenPlayers.delete(p.userId);
        }
      });
  }

  /**
   * Get the number of currently hidden players
   * @returns Count of hidden players
   */
  public get hiddenPlayersCount(): number {
    return this.hiddenPlayers.size;
  }

  /**
   * Check if a player is currently hidden due to culling
   * @param userId - User ID to check
   * @returns true if player is hidden
   */
  public isPlayerHidden(userId: string): boolean {
    return this.hiddenPlayers.has(userId);
  }

  /**
   * Configure the padding used for visibility calculations.
   * Larger padding means players will be visible further outside the viewport.
   * @param padding - Padding in pixels
   */
  public setPadding(padding: number): void {
    this.padding = padding;
  }
}
